/*
  Photo filters as color matrices, the same math as SVG's feColorMatrix: each output channel is a weighted sum
  of the input channels plus an offset. Pixels are transformed directly because Safari ships the canvas `filter`
  property disabled, so CSS filters would not reach the shared image there.

  A matrix is three rows (red, green, blue), each [r, g, b, offset] with the offset in 0-255 units. Alpha is
  never touched.

  Some filters then blend a gradient over the photo, like a CSS overlay with `mix-blend-mode`. Canvas blend modes
  work in every browser, so the overlay is drawn as is.
*/

const IDENTITY = [
  [1, 0, 0, 0],
  [0, 1, 0, 0],
  [0, 0, 1, 0],
];

const SEPIA = [
  [0.393, 0.769, 0.189, 0],
  [0.349, 0.686, 0.168, 0],
  [0.272, 0.534, 0.131, 0],
];

// Rec. 709 luminance weights, as feColorMatrix's `saturate` uses.
const LUMINANCE = [0.2126, 0.7152, 0.0722];

const saturation = (amount) => IDENTITY.map((row, channel) => [
  ...LUMINANCE.map((weight, input) => weight * (1 - amount) + (input === channel ? amount : 0)),
  0,
]);

const contrast = (amount) => IDENTITY.map((row) => [
  ...row.slice(0, 3).map((value) => value * amount),
  127.5 * (1 - amount),
]);

const brightness = (amount) => IDENTITY.map((row) => [...row.slice(0, 3).map((value) => value * amount), 0]);

const sepia = (amount) => IDENTITY.map((row, channel) => row.map(
  (value, column) => value * (1 - amount) + SEPIA[channel][column] * amount,
));

// The matrix that applies `first`, then `second`.
const compose = (first, second) => second.map(([r, g, b, offset]) => [0, 1, 2, 3].map((column) => (
  r * first[0][column] + g * first[1][column] + b * first[2][column] + (column === 3 ? offset : 0)
)));

// The matrix that applies each of `matrices` in order.
const combine = (...matrices) => matrices.reduce(compose, IDENTITY);

/*
  An overlay is a gradient and the blend mode it is drawn with. Stops are [offset 0-1, color]; a fading stop keeps
  its color at zero alpha, because canvas fades `transparent` through black.
*/
const linearOverlay = (blend, angle, stops) => ({ blend, gradient: { type: 'linear', angle, stops } });
const radialOverlay = (blend, center, stops) => ({ blend, gradient: { type: 'radial', center, stops } });
const CENTER = { x: 0.5, y: 0.5 };

const FILTERS = [
  { id: 'original', matrix: IDENTITY },
  {
    id: 'rouge',
    matrix: combine(contrast(1.1), saturation(1.15)),
    overlay: linearOverlay('soft-light', 180, [[0, 'rgba(243, 180, 214, 0.5)'], [1, 'rgba(190, 24, 93, 0.4)']]),
  },
  {
    id: 'glow',
    matrix: combine(brightness(1.07), saturation(1.25), contrast(1.03)),
    overlay: radialOverlay('screen', { x: 0.3, y: 0.18 }, [
      [0, 'rgba(255, 214, 180, 0.55)'],
      [0.62, 'rgba(255, 214, 180, 0)'],
    ]),
  },
  {
    id: 'golden',
    matrix: combine(sepia(0.28), saturation(1.4), brightness(1.04)),
    overlay: linearOverlay('soft-light', 160, [[0, 'rgba(255, 170, 60, 0.4)'], [1, 'rgba(140, 30, 40, 0.3)']]),
  },
  {
    id: 'iron',
    matrix: combine(saturation(0), contrast(1.3), brightness(1.03)),
    overlay: radialOverlay('multiply', CENTER, [[0.55, 'rgba(0, 0, 0, 0)'], [1, 'rgba(0, 0, 0, 0.45)']]),
  },
  {
    id: 'grit',
    matrix: combine(contrast(1.45), saturation(1.2), brightness(0.93)),
    overlay: radialOverlay('multiply', CENTER, [[0.45, 'rgba(40, 0, 6, 0)'], [1, 'rgba(40, 0, 6, 0.65)']]),
  },
];

/**
 * Where a gradient runs on a `width` × `height` image, as CSS draws it: a linear one along its angle (0 is up,
 * 90 is right) across the whole box, a radial one as a circle reaching the farthest corner.
 *
 * @param {Object} gradient An overlay's gradient.
 * @param {number} width
 * @param {number} height
 * @returns {{ x0: number, y0: number, x1: number, y1: number }|{ x: number, y: number, radius: number }}
 */
const getGradientGeometry = (gradient, width, height) => {
  if (gradient.type === 'radial') {
    const x = gradient.center.x * width;
    const y = gradient.center.y * height;
    return { x, y, radius: Math.hypot(Math.max(x, width - x), Math.max(y, height - y)) };
  }
  const radians = (gradient.angle * Math.PI) / 180;
  const dx = Math.sin(radians);
  const dy = -Math.cos(radians);
  const halfLength = (Math.abs(width * dx) + Math.abs(height * dy)) / 2;
  return {
    x0: width / 2 - dx * halfLength,
    y0: height / 2 - dy * halfLength,
    x1: width / 2 + dx * halfLength,
    y1: height / 2 + dy * halfLength,
  };
};

const getFilter = (filterId) => FILTERS.find(({ id }) => id === filterId) || FILTERS[0];

/**
 * Applies a color matrix to RGBA pixels. The clamped array rounds each value and holds it to 0-255.
 *
 * @param {Uint8ClampedArray} pixels RGBA pixels, as `ImageData.data` holds them.
 * @param {number[][]} matrix
 * @returns {Uint8ClampedArray} The filtered pixels, in a new array.
 */
const applyColorMatrix = (pixels, matrix) => {
  const [red, green, blue] = matrix;
  const filtered = new Uint8ClampedArray(pixels.length);
  for (let i = 0; i < pixels.length; i += 4) {
    const r = pixels[i];
    const g = pixels[i + 1];
    const b = pixels[i + 2];
    filtered[i] = red[0] * r + red[1] * g + red[2] * b + red[3];
    filtered[i + 1] = green[0] * r + green[1] * g + green[2] * b + green[3];
    filtered[i + 2] = blue[0] * r + blue[1] * g + blue[2] * b + blue[3];
    filtered[i + 3] = pixels[i + 3];
  }
  return filtered;
};

export {
  FILTERS,
  getFilter,
  getGradientGeometry,
  applyColorMatrix,
};
